import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

const LAST_UPDATE = "28 de agosto de 2026";
const COMPANY_NAME = "ACERTO RÁPIDO JVO";
const CONTACT_EMAIL = "contato@acertorapidojvo.com.br";

function LegalLayout({ title, children }) {
  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo size={40} /><span className="font-extrabold text-blue-900 text-sm hidden sm:inline" style={{ fontFamily: "Manrope" }}>ACERTO RÁPIDO JVO</span></Link>
          <div className="flex gap-4 text-sm">
            <Link to="/termos" className="text-slate-700 hover:text-blue-700 font-semibold">Termos</Link>
            <Link to="/privacidade" className="text-slate-700 hover:text-blue-700 font-semibold">Privacidade</Link>
            <Link to="/" className="text-blue-700 font-semibold">Home</Link>
          </div>
        </div>
      </nav>
      <article className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>{title}</h1>
        <p className="text-xs text-slate-500 mt-1">Última atualização: {LAST_UPDATE}</p>
        <div className="prose prose-slate mt-8 max-w-none space-y-4 text-slate-700 leading-relaxed">
          {children}
        </div>
      </article>
      <footer className="border-t bg-slate-950 text-slate-400 py-6">
        <div className="max-w-4xl mx-auto px-6 text-xs flex flex-col md:flex-row items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} {COMPANY_NAME}</span>
          <div className="flex gap-4">
            <Link to="/termos" className="hover:text-white">Termos</Link>
            <Link to="/privacidade" className="hover:text-white">Privacidade</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white">{CONTACT_EMAIL}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function Termos() {
  return (
    <LegalLayout title="Termos de Uso">
      <p><strong>Ao criar uma conta e utilizar o {COMPANY_NAME} (o "Serviço"), você concorda com estes Termos.</strong> Leia com atenção. Se não concordar, não use o Serviço.</p>

      <h2 className="text-xl font-bold mt-8">1. Objeto</h2>
      <p>O Serviço é uma plataforma web e mobile (PWA) que oferece ferramentas de gestão de viagens, fretes, abastecimentos, despesas, manutenção e acerto financeiro para motoristas autônomos, motoristas empregados e empresas transportadoras.</p>

      <h2 className="text-xl font-bold mt-8">2. Cadastro e Conta</h2>
      <p>2.1. Para usar o Serviço, você deve criar uma conta fornecendo dados verdadeiros e atualizados. Você é responsável por manter a confidencialidade da sua senha.</p>
      <p>2.2. O uso do Serviço é restrito a maiores de 18 anos. Ao se cadastrar, você declara ter idade legal para celebrar contratos no Brasil.</p>
      <p>2.3. Você é o único responsável por todas as atividades realizadas com sua conta.</p>

      <h2 className="text-xl font-bold mt-8">3. Planos, Preços e Pagamento</h2>
      <p>3.1. O Serviço oferece <strong>7 dias de uso gratuito (trial)</strong> sem exigência de cartão. Após o trial, se não houver assinatura ativa, a conta e todos os dados vinculados são <strong>excluídos automaticamente</strong> — deixando o e-mail livre para novo cadastro futuro.</p>
      <p>3.2. Os preços vigentes estão publicados em <Link to="/#precos" className="text-blue-700 underline">nossa página de preços</Link>. Formas de pagamento aceitas: PIX à vista mensal, cartão de crédito e débito (parcelamento em até 3x no plano anual).</p>
      <p>3.3. <strong>PIX à vista mensal:</strong> enviamos aviso por e-mail 8 dias antes do vencimento com o código PIX. Em caso de atraso superior a 5 dias, o acesso ao Serviço será suspenso automaticamente até a regularização. Ao confirmar o pagamento, o acesso é reativado em segundos.</p>
      <p>3.4. <strong>Cartão de crédito:</strong> cobrança automática recorrente. Você pode cancelar a qualquer momento no dashboard "Meu Plano".</p>
      <p>3.5. Todos os pagamentos são processados por parceiros de mercado (Stripe), sob suas próprias políticas de segurança PCI-DSS. Não armazenamos dados de cartão em nossos servidores.</p>

      <h2 className="text-xl font-bold mt-8">4. Cancelamento e Reembolso</h2>
      <p>4.1. Você pode cancelar sua assinatura a qualquer momento pelo painel "Meu Plano". O acesso permanece ativo até o fim do período pago.</p>
      <p>4.2. Reembolsos são analisados caso a caso e concedidos apenas em situações de erro comprovado do Serviço, no prazo de até 7 dias após a cobrança.</p>

      <h2 className="text-xl font-bold mt-8">5. Programa de Indicação (Indique-e-Ganhe)</h2>
      <p>5.1. Cada usuário recebe um código único para indicar amigos. Quando o indicado assina o plano pago, o indicador e o indicado recebem, cada um, um cupom de <strong>50% OFF</strong> aplicável ao próximo pagamento.</p>
      <p>5.2. O limite é de 3 (três) indicações válidas por conta.</p>
      <p>5.3. Só contam indicações onde o indicado efetivamente pagou a primeira mensalidade — o trial de 7 dias não gera cupom.</p>
      <p>5.4. O {COMPANY_NAME} pode alterar ou encerrar o programa com aviso prévio de 30 dias.</p>

      <h2 className="text-xl font-bold mt-8">6. Uso Aceitável</h2>
      <p>Você concorda em NÃO: (a) usar o Serviço para fins ilícitos; (b) tentar acessar dados de outros usuários; (c) fazer engenharia reversa ou copiar o código; (d) usar bots ou scripts automatizados; (e) revender o acesso sem autorização por escrito.</p>

      <h2 className="text-xl font-bold mt-8">7. Propriedade Intelectual</h2>
      <p>Todo o conteúdo, marca, código, logotipos e design do Serviço são propriedade do {COMPANY_NAME}. Os dados que você insere (viagens, fretes, documentos) permanecem seus.</p>

      <h2 className="text-xl font-bold mt-8">8. Disponibilidade e Limitação de Responsabilidade</h2>
      <p>8.1. Nos esforçamos para manter o Serviço disponível 24/7, mas não garantimos disponibilidade ininterrupta. Podem ocorrer paradas para manutenção ou por causas alheias.</p>
      <p>8.2. O {COMPANY_NAME} não se responsabiliza por decisões comerciais tomadas com base nos relatórios do Serviço. Você é responsável por conferir os cálculos antes de repassar valores.</p>
      <p>8.3. A responsabilidade total do {COMPANY_NAME}, em qualquer hipótese, limita-se ao valor pago pelo assinante nos últimos 12 meses.</p>

      <h2 className="text-xl font-bold mt-8">9. Encerramento</h2>
      <p>Podemos suspender ou encerrar sua conta em caso de: (a) inadimplência superior a 30 dias; (b) violação destes Termos; (c) uso fraudulento. Antes de encerrar, enviaremos aviso por e-mail com prazo mínimo de 7 dias, exceto em casos de fraude comprovada.</p>

      <h2 className="text-xl font-bold mt-8">10. Alterações</h2>
      <p>Podemos atualizar estes Termos periodicamente. Alterações relevantes serão comunicadas por e-mail e pelo próprio aplicativo com no mínimo 30 dias de antecedência.</p>

      <h2 className="text-xl font-bold mt-8">11. Lei Aplicável e Foro</h2>
      <p>Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o foro da comarca de residência do consumidor para dirimir controvérsias.</p>

      <h2 className="text-xl font-bold mt-8">12. Contato</h2>
      <p>Dúvidas sobre estes Termos? Fale conosco: <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-700 underline">{CONTACT_EMAIL}</a></p>
    </LegalLayout>
  );
}

export function Privacidade() {
  return (
    <LegalLayout title="Política de Privacidade (LGPD)">
      <p>Esta Política descreve como o <strong>{COMPANY_NAME}</strong> coleta, usa, armazena e protege seus dados pessoais, em conformidade com a <strong>Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD)</strong>.</p>

      <h2 className="text-xl font-bold mt-8">1. Controlador dos Dados</h2>
      <p>O controlador dos dados pessoais tratados pelo Serviço é o {COMPANY_NAME}, com contato pelo e-mail <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-700 underline">{CONTACT_EMAIL}</a>.</p>

      <h2 className="text-xl font-bold mt-8">2. Quais dados coletamos</h2>
      <p><strong>Dados fornecidos por você:</strong></p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Nome, e-mail, telefone, CPF/CNPJ (identificação)</li>
        <li>Senha (armazenada apenas em formato criptografado — hash bcrypt)</li>
        <li>Dados da empresa/veículos: razão social, placa, modelo, motorista responsável</li>
        <li>Dados operacionais: fretes, abastecimentos, KMs, valores, rotas, GPS quando ativado</li>
        <li>Documentos anexados (canhotos, notas fiscais, recibos de pedágio)</li>
        <li>Foto de perfil (opcional)</li>
      </ul>
      <p><strong>Dados coletados automaticamente:</strong></p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Endereço IP e informações do dispositivo (navegador, sistema operacional)</li>
        <li>Data e hora de acesso, páginas visitadas, cookies de sessão essenciais</li>
        <li>Localização GPS <em>apenas se você autorizar</em> o rastreamento no dispositivo</li>
      </ul>

      <h2 className="text-xl font-bold mt-8">3. Bases legais e finalidades</h2>
      <p>Tratamos seus dados com base em (art. 7º da LGPD):</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Execução de contrato</strong> — prestação do Serviço, autenticação, cobrança, relatórios</li>
        <li><strong>Consentimento</strong> — envio de e-mails de marketing (você pode revogar a qualquer momento)</li>
        <li><strong>Obrigação legal</strong> — emissão fiscal, guarda de registros</li>
        <li><strong>Legítimo interesse</strong> — segurança, prevenção de fraude, melhoria do Serviço</li>
      </ul>

      <h2 className="text-xl font-bold mt-8">4. Compartilhamento com terceiros</h2>
      <p>Compartilhamos apenas o mínimo necessário com fornecedores essenciais, sob contrato de confidencialidade:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Stripe</strong> — processamento de pagamentos (dados de cartão e PIX ficam apenas com o Stripe)</li>
        <li><strong>Resend</strong> — envio de e-mails transacionais (boas-vindas, cobrança, alertas)</li>
        <li><strong>Emergent / MongoDB Atlas</strong> — infraestrutura de hospedagem e banco de dados</li>
        <li><strong>Google / IBGE APIs</strong> — busca de UF/cidade, mapas (dados agregados anônimos)</li>
      </ul>
      <p>Não vendemos nem cedemos seus dados a terceiros para fins publicitários.</p>

      <h2 className="text-xl font-bold mt-8">5. Retenção</h2>
      <p>Mantemos seus dados enquanto sua conta estiver ativa. Após o cancelamento, mantemos por até <strong>5 anos</strong> registros fiscais e contábeis exigidos por lei. Os demais dados podem ser excluídos mediante pedido.</p>

      <h2 className="text-xl font-bold mt-8">6. Seus direitos (art. 18 LGPD)</h2>
      <p>A qualquer momento você pode:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Confirmar se tratamos seus dados</li>
        <li>Acessar, corrigir ou atualizar seus dados</li>
        <li>Solicitar anonimização, bloqueio ou eliminação de dados desnecessários</li>
        <li>Solicitar portabilidade dos dados</li>
        <li>Revogar consentimento (para e-mails de marketing, GPS, etc.)</li>
        <li>Peticionar contra nós à Autoridade Nacional de Proteção de Dados (ANPD)</li>
      </ul>
      <p>Para exercer qualquer direito, envie um e-mail para <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-700 underline">{CONTACT_EMAIL}</a> — respondemos em até 15 dias úteis.</p>

      <h2 className="text-xl font-bold mt-8">7. Segurança</h2>
      <p>Adotamos medidas técnicas e organizacionais para proteger seus dados:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Conexão HTTPS/TLS em todo o tráfego</li>
        <li>Senhas armazenadas com hash bcrypt (irreversível)</li>
        <li>Segregação de dados por conta (tenancy) — cada empresa/motorista só vê os próprios dados</li>
        <li>Backups criptografados e monitoramento contínuo</li>
        <li>Acesso interno restrito ao mínimo pessoal necessário</li>
      </ul>

      <h2 className="text-xl font-bold mt-8">8. Cookies</h2>
      <p>Usamos apenas cookies essenciais para autenticação e funcionamento do Serviço. Não usamos cookies de terceiros para publicidade.</p>

      <h2 className="text-xl font-bold mt-8">9. Menores de idade</h2>
      <p>O Serviço é destinado a maiores de 18 anos. Não coletamos intencionalmente dados de menores.</p>

      <h2 className="text-xl font-bold mt-8">10. Alterações desta Política</h2>
      <p>Podemos atualizar esta Política periodicamente. Alterações substanciais serão comunicadas por e-mail com no mínimo 30 dias de antecedência.</p>

      <h2 className="text-xl font-bold mt-8">11. Encarregado de Dados (DPO)</h2>
      <p>O nosso Encarregado de Proteção de Dados pode ser contatado em: <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-700 underline">{CONTACT_EMAIL}</a></p>
    </LegalLayout>
  );
}
